import type { PublicOptions } from '@pa/api/public'
import { Avatar, Select } from '@pa/ui'
import { ArrowRight, Check, MapPin, Stethoscope, Video } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { ScreenTitle } from '@/features/entry'
import { isComplete, lockedChoice, locationsOf, providersFor, visitTypesAt, type Choice } from '../model/resolve'

/** A value the patient can't change (the only option): shown as already selected (mockup `FixedRow`). */
function FixedRow({ icon, label, value, sub }: { icon: ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="fixed-row" role="group" aria-label={`${label}: ${value} (selected)`}>
      <span className="fr-ic" aria-hidden>
        {icon}
      </span>
      <div className="grow">
        <span className="fr-label">{label}</span>
        <b>{value}</b>
        {sub !== undefined && sub !== '' && <span className="fr-sub">{sub}</span>}
      </div>
      <span className="fr-check" aria-hidden>
        <Check />
      </span>
    </div>
  )
}

const withCredentials = (p: { name: string; credentials: string }) =>
  [p.name, p.credentials].filter((v) => v !== '').join(', ')

/**
 * P-07 "Where would you like to be seen?" (UserFlows P-07), ported from the mockup's `patient/steps.tsx` `VisitStep`
 * without the demo items: Location cards, the visit type select, provider cards; single options become fixed rows;
 * the heading names what is still open; "Choose a time" is enabled once all three are chosen. There is no "any
 * provider" option. A **locked** link preset (task 22, D-79) shows its parts as fixed rows ("Your office", "Your
 * visit", "You'll see") with no controls, even before the office is picked; the server already filtered the lists.
 */
export function VisitStep({
  options,
  choice,
  stepLabel,
  onChoose,
  onNext,
}: {
  options: PublicOptions
  choice: Choice
  stepLabel: string
  onChoose: (patch: Partial<Choice>) => void
  onNext: () => void
}) {
  const id = useId()
  const locked = lockedChoice(options)
  const lockedType = options.visitTypes.find((t) => t.id === locked.visitTypeId)
  const lockedProvider = options.providers.find((p) => p.id === locked.providerId)
  const locs = locationsOf(options)
  const chooseLocation = locs.length > 1
  const location = locs.find((l) => l.id === choice.locationId)
  const types = choice.locationId ? visitTypesAt(options, choice.locationId) : []
  const chooseType = lockedType === undefined && types.length > 1
  const type = lockedType ?? types.find((t) => t.id === choice.visitTypeId)
  const tele = type?.telehealth === true
  const provs =
    choice.locationId && choice.visitTypeId ? providersFor(options, choice.locationId, choice.visitTypeId) : []
  const fixedProvider =
    lockedProvider ?? (provs.length === 1 && provs[0]?.id === choice.providerId ? provs[0] : undefined)
  const chooseProvider = fixedProvider === undefined && provs.length > 1

  const title = !choice.locationId
    ? 'Where would you like to be seen?'
    : !choice.visitTypeId
      ? 'What kind of visit do you need?'
      : 'Who would you like to see?'
  const todo = [
    chooseLocation && 'an office',
    chooseType && 'the kind of visit',
    fixedProvider === undefined && (chooseProvider || !choice.visitTypeId) && "who you'd like to see",
  ].filter((v): v is string => typeof v === 'string')
  const sub =
    todo.length > 1
      ? `Choose ${todo.slice(0, -1).join(', ')} and ${todo[todo.length - 1] ?? ''}.`
      : todo.length === 1
        ? `Choose ${todo[0] ?? ''}.`
        : undefined

  return (
    <>
      <div>
        <div className="pt-step-label">{stepLabel}</div>
        <ScreenTitle className="pt-title">{title}</ScreenTitle>
        {sub !== undefined && <p className="pt-sub">{sub}</p>}
      </div>
      <div className="pt-body visit-step">
        <div>
          <h2 className="section-label" id={`${id}-loc`}>
            Location
          </h2>
          {chooseLocation ? (
            <div className="loc-grid" role="group" aria-labelledby={`${id}-loc`}>
              {locs.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="loc-card"
                  aria-pressed={choice.locationId === l.id}
                  onClick={() => {
                    onChoose({ locationId: l.id })
                  }}
                >
                  <MapPin aria-hidden />
                  <div>
                    <b>{l.name}</b>
                    {l.address !== '' && <span>{l.address}</span>}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            location && <FixedRow icon={<MapPin />} label="Your office" value={location.name} sub={location.address} />
          )}
        </div>

        <div>
          <h2 className="section-label" id={`${id}-type`}>
            Visit type
          </h2>
          {!choice.locationId && !lockedType ? (
            <div className="step-hint">Choose a location first to see the visits offered there.</div>
          ) : chooseType ? (
            <Select
              value={choice.visitTypeId}
              placeholder="Choose a visit type"
              labelledBy={`${id}-type`}
              onChange={(v) => {
                onChoose({ visitTypeId: v })
              }}
              options={types.map((t) => ({
                value: t.id,
                label: t.name,
                ...(t.telehealth ? { sub: 'Video visit from home' } : {}),
                icon: <span className="vt-ic">{t.telehealth ? <Video size={16} /> : <Stethoscope size={16} />}</span>,
              }))}
            />
          ) : (
            type && (
              <FixedRow
                icon={tele ? <Video size={18} /> : <Stethoscope size={18} />}
                label="Your visit"
                value={type.name}
                {...(tele ? { sub: 'Video visit from home' } : {})}
              />
            )
          )}
          {tele && (
            <div className="info-callout">
              <Video aria-hidden /> This is a video visit: you&apos;ll join from home. We&apos;ll send you a secure link
              before your appointment.
            </div>
          )}
        </div>

        <div>
          <h2 className="section-label" id={`${id}-prov`}>
            Provider
          </h2>
          {fixedProvider ? (
            <FixedRow
              icon={<Avatar name={fixedProvider.name} color={fixedProvider.color} size="sm" />}
              label="You'll see"
              value={withCredentials(fixedProvider)}
              sub={fixedProvider.specialty}
            />
          ) : chooseProvider ? (
            <div className="provider-grid" role="group" aria-labelledby={`${id}-prov`}>
              {provs.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="provider-card"
                  aria-pressed={choice.providerId === p.id}
                  onClick={() => {
                    onChoose({ providerId: p.id })
                  }}
                >
                  <span className="check" aria-hidden>
                    <Check />
                  </span>
                  <Avatar name={p.name} color={p.color} size="lg" />
                  <div>
                    <b>{withCredentials(p)}</b>
                    {p.specialty !== '' && <span className="spec">{p.specialty}</span>}
                  </div>
                  {p.bio !== '' && <p>{p.bio}</p>}
                </button>
              ))}
            </div>
          ) : (
            <div className="step-hint">
              {choice.locationId
                ? 'Choose a visit type to see who can see you.'
                : 'Choose a location and visit type to see who can see you.'}
            </div>
          )}
        </div>
      </div>
      <div className="pt-actions">
        <span className="spacer" />
        <button type="button" className="btn btn-primary btn-lg" disabled={!isComplete(choice)} onClick={onNext}>
          Choose a time <ArrowRight aria-hidden />
        </button>
      </div>
    </>
  )
}
